import React, { useEffect, useMemo, useState } from 'react';

export default function SectionBookmarkModal({ open, onClose, API_URL }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saveForLater, setSaveForLater] = useState(false);
  const [savedListOpen, setSavedListOpen] = useState(false);
  const [savedItems, setSavedItems] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedError, setSavedError] = useState('');
  const [selectedSaved, setSelectedSaved] = useState(() => new Set());
  const [printBusy, setPrintBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [savedSortBy, setSavedSortBy] = useState('name');
  const [savedSortDir, setSavedSortDir] = useState('asc');
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const sortButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit' };

  const sortedSavedItems = useMemo(() => {
    const list = [...savedItems];
    list.sort((a, b) => {
      let comp = 0;
      if (savedSortBy === 'created_at') {
        const at = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
        comp = (Number.isFinite(at) ? at : 0) - (Number.isFinite(bt) ? bt : 0);
      } else {
        comp = (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      }
      return savedSortDir === 'desc' ? comp * -1 : comp;
    });
    return list;
  }, [savedItems, savedSortBy, savedSortDir]);

  const handleSort = (column) => {
    if (savedSortBy === column) {
      setSavedSortDir(dir => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSavedSortBy(column);
      setSavedSortDir('asc');
    }
  };

  const renderSortIndicator = (column) => {
    if (savedSortBy !== column) {
      return <span style={{ fontSize: 12, color: '#999' }}>⇅</span>;
    }
    return (
      <span style={{ fontSize: 12, color: '#0070f3' }}>
        {savedSortDir === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  useEffect(() => {
    if (savedListOpen) {
      loadSaved();
    } else {
      setSavedError('');
      setSelectedSaved(new Set());
      setEditingId(null);
      setEditingValue('');
      setEditBusy(false);
      setDeleteBusyId(null);
    }
  }, [savedListOpen]);

  if (!open) return null;

  const authHeaders = () => {
    const token = localStorage.getItem('auth_token') || '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadSaved = async () => {
    setEditingId(null);
    setEditingValue('');
    setEditBusy(false);
    setDeleteBusyId(null);
    setSavedLoading(true);
    setSavedError('');
    try {
      const res = await fetch(`${API_URL}/cabinet/bookmarks`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.message || 'Failed to load saved sections');
      }
      const data = await res.json().catch(() => []);
      setSavedItems(Array.isArray(data) ? data : []);
      setSelectedSaved(new Set());
    } catch (e) {
      setSavedError(e?.message || 'Failed to load saved sections');
    } finally {
      setSavedLoading(false);
    }
  };

  const saveBookmark = async (sectionName) => {
    const res = await fetch(`${API_URL}/cabinet/bookmarks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ name: sectionName })
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.message || 'Failed to save bookmark');
    }
    const payload = await res.json().catch(() => ({}));
    if (savedListOpen) loadSaved();
    return payload?.bookmark || null;
  };

  const handleCreate = async () => {
    const title = String(name || '').trim();
    if (!title) { setError('Please enter a section name.'); return; }
    setBusy(true); setError('');
    try {
      if (saveForLater) {
        await saveBookmark(title);
      }
      const token = localStorage.getItem('auth_token') || '';
      const res = await fetch(`${API_URL}/barcode/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ values: [`--${title}--`] })
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.message || 'Failed to generate bookmark sheet');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'noopener');
      if (!w) {
        alert('Popup blocked. Please allow popups to print the bookmark sheet.');
      }
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      setName('');
      setSaveForLater(false);
      onClose && onClose(true);
    } catch (e) {
      setError(e?.message || 'Failed to generate');
    } finally {
      setBusy(false);
    }
  };

  const toggleSaved = (id) => {
    setSelectedSaved(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllSaved = () => {
    setSelectedSaved(prev => {
      if (prev.size === savedItems.length) return new Set();
      return new Set(savedItems.map(item => item.id));
    });
  };

  const handlePrintSaved = async () => {
    if (!savedItems.length) {
      setSavedError('No saved sections available to print.');
      return;
    }
    const selected = savedItems.filter(item => selectedSaved.has(item.id));
    if (!selected.length) {
      setSavedError('Select at least one section to print.');
      return;
    }
    setPrintBusy(true);
    setSavedError('');
    try {
      const res = await fetch(`${API_URL}/cabinet/bookmarks/print`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ names: selected.map(item => item.name) })
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.message || 'Failed to print saved sections');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'noopener');
      if (!w) {
        alert('Popup blocked. Please allow popups to print the saved sections.');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setSavedError(e?.message || 'Failed to print saved sections');
    } finally {
      setPrintBusy(false);
    }
  };

  const startEditing = (item) => {
    if (!item || editBusy) return;
    setEditingId(item.id);
    setEditingValue(item.name);
    setSavedError('');
  };

  const cancelEditing = () => {
    if (editBusy) return;
    setEditingId(null);
    setEditingValue('');
  };

  const saveEditedName = async () => {
    if (editingId === null) return;
    const title = String(editingValue || '').trim();
    if (!title) {
      setSavedError('Please enter a section name.');
      return;
    }
    setEditBusy(true);
    setSavedError('');
    try {
      const res = await fetch(`${API_URL}/cabinet/bookmarks/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: title })
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.message || 'Failed to update section');
      }
      const payload = await res.json().catch(() => ({}));
      const updated = payload?.bookmark || null;
      if (updated) {
        setSavedItems(prev => prev.map(item => (
          item.id === updated.id ? { ...item, name: updated.name, created_at: updated.created_at } : item
        )));
      }
      setEditingId(null);
      setEditingValue('');
    } catch (e) {
      setSavedError(e?.message || 'Failed to update section');
    } finally {
      setEditBusy(false);
    }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEditedName();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  };

  const handleDelete = async (item) => {
    if (!item || deleteBusyId) return;
    if (!window.confirm(`Delete saved section "${item.name}"?`)) return;
    setDeleteBusyId(item.id);
    setSavedError('');
    try {
      const res = await fetch(`${API_URL}/cabinet/bookmarks/${item.id}`, {
        method: 'DELETE',
        headers: { ...authHeaders() }
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.message || 'Failed to delete section');
      }
      setSavedItems(prev => prev.filter(entry => entry.id !== item.id));
      setSelectedSaved(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      if (editingId === item.id) {
        setEditingId(null);
        setEditingValue('');
        setEditBusy(false);
      }
    } catch (e) {
      setSavedError(e?.message || 'Failed to delete section');
    } finally {
      setDeleteBusyId(null);
    }
  };

  const closeAll = () => {
    setName('');
    setSaveForLater(false);
    setError('');
    onClose && onClose(false);
  };

  return (
    <>
      <div className="upload-spinner-overlay" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="upload-spinner-box" style={{ width: 480, maxWidth: '95vw', textAlign: 'left' }}>
          <h3 style={{ marginTop: 0 }}>Section Bookmark Sheet</h3>
          <p style={{ marginTop: 0, color: 'var(--text)' }}>
            Enter a Section Name to generate a printable separator sheet with a bookmark barcode
            in the format <code>--BOOKMARK NAME--</code>.
          </p>
          <div style={{ marginTop: 8 }}>
            <label style={{ fontSize: 12 }}>Section Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
              placeholder="e.g., Loan Documents"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-soft)', background: 'var(--input-bg)', color: 'var(--text)' }}
            />
          </div>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              id="bookmark-save-toggle"
              type="checkbox"
              checked={saveForLater}
              onChange={e => setSaveForLater(e.target.checked)}
            />
            <label htmlFor="bookmark-save-toggle" style={{ fontSize: 13, cursor: 'pointer' }}>
              Save for later use
            </label>
          </div>
          {error && <div style={{ marginTop: 10, color: '#b71c1c' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button className="btn btn-ghost" disabled={busy} onClick={closeAll}>Close</button>
            <button className="btn btn-ghost" disabled={busy} onClick={() => setSavedListOpen(true)}>Saved List</button>
            <button className="btn" disabled={busy} onClick={handleCreate}>{busy ? 'Generating…' : 'Generate & Print'}</button>
          </div>
        </div>
      </div>

      {savedListOpen && (
        <div className="upload-spinner-overlay" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="upload-spinner-box" style={{ width: 560, maxWidth: '95vw', textAlign: 'left' }}>
            <h3 style={{ marginTop: 0 }}>Saved Sections</h3>
            <p style={{ marginTop: 0, fontSize: 13, color: 'var(--text)' }}>
              Select one or more saved bookmarks to print a combined PDF of their separator sheets.
            </p>
            {savedError && <div style={{ marginBottom: 10, color: '#b71c1c' }}>{savedError}</div>}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="btn btn-ghost" onClick={loadSaved} disabled={savedLoading}>{savedLoading ? 'Refreshing…' : 'Refresh'}</button>
              <button className="btn btn-ghost" onClick={selectAllSaved} disabled={!savedItems.length}>
                {selectedSaved.size === savedItems.length && savedItems.length ? 'Clear Selection' : 'Select All'}
              </button>
            </div>
            <div style={{ maxHeight: '45vh', overflowY: 'auto', border: '1px solid var(--border-soft)', borderRadius: 6 }}>
              {savedLoading ? (
                <div style={{ padding: 20, textAlign: 'center' }}>Loading saved sections…</div>
              ) : savedItems.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#777' }}>No saved sections yet.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--hover-bg)' }}>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Select</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>
                        <button type="button" style={sortButtonStyle} onClick={() => handleSort('name')} disabled={savedLoading}>
                          Section Name {renderSortIndicator('name')}
                        </button>
                      </th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>
                        <button type="button" style={sortButtonStyle} onClick={() => handleSort('created_at')} disabled={savedLoading}>
                          Saved {renderSortIndicator('created_at')}
                        </button>
                      </th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSavedItems.map(item => (
                      <tr key={item.id} style={{ borderTop: '1px solid var(--border-soft)' }}>
                        <td style={{ padding: '8px' }}>
                          <input
                            type="checkbox"
                            checked={selectedSaved.has(item.id)}
                            onChange={() => toggleSaved(item.id)}
                          />
                        </td>
                        <td style={{ padding: '8px', minWidth: 220 }}>
                          {editingId === item.id ? (
                            <input
                              type="text"
                              value={editingValue}
                              onChange={e => setEditingValue(e.target.value)}
                              onKeyDown={handleEditKeyDown}
                              disabled={editBusy}
                              autoFocus
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                borderRadius: 4,
                                border: '1px solid var(--border-soft)',
                                background: 'var(--input-bg)',
                                color: 'var(--text)'
                              }}
                            />
                          ) : (
                            item.name
                          )}
                        </td>
                        <td style={{ padding: '8px', fontSize: 12, color: '#777' }}>
                          {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}
                        </td>
                        <td style={{ padding: '8px', minWidth: 200 }}>
                          {editingId === item.id ? (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button className="btn" style={{ padding: '4px 12px' }} onClick={saveEditedName} disabled={editBusy}>
                                  {editBusy ? 'Saving…' : 'Save'}
                                </button>
                                <button className="btn btn-ghost" style={{ padding: '4px 12px' }} onClick={cancelEditing} disabled={editBusy}>
                                  Cancel
                                </button>
                              </div>
                              <button
                                className="btn btn-ghost"
                                style={{ padding: '4px 12px', color: '#b71c1c' }}
                                onClick={() => handleDelete(item)}
                                disabled={deleteBusyId === item.id || editBusy}
                              >
                                {deleteBusyId === item.id ? 'Deleting…' : 'Delete'}
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button
                                className="btn btn-ghost"
                                style={{ padding: '4px 12px' }}
                                onClick={() => startEditing(item)}
                                disabled={editBusy || savedLoading}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-ghost"
                                style={{ padding: '4px 12px', color: '#b71c1c' }}
                                onClick={() => handleDelete(item)}
                                disabled={deleteBusyId === item.id}
                              >
                                {deleteBusyId === item.id ? 'Deleting…' : 'Delete'}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button className="btn btn-ghost" onClick={() => setSavedListOpen(false)} disabled={printBusy}>Close</button>
              <button className="btn" onClick={handlePrintSaved} disabled={printBusy || savedLoading || savedItems.length === 0}>
                {printBusy ? 'Preparing…' : 'Print Selected'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
